import { useEffect, useState, useCallback, useRef } from 'react';
import {
  useMsal,
  useAccount,
  AuthenticatedTemplate,
  UnauthenticatedTemplate
} from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus, CacheLookupPolicy } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { loginRequest, apiTokenRequest, graphConfig } from '../auth/authConfig';
import { waitForAccount } from '../auth/utils';
import type { UserProfile, AuthState } from '../auth/types';
import { logger } from '../utils/logger';
import { clearTelemetryUser } from '../utils/errorReporting';

/**
 * Module-level mutex to prevent multiple simultaneous acquireTokenRedirect calls.
 * When one redirect is in progress, all other callers wait for it instead of
 * triggering their own redirect (which MSAL would reject with interaction_in_progress).
 */
let redirectInProgress: Promise<void> | null = null;

const extractRolesFromClaims = (claims: Record<string, unknown> | undefined): readonly string[] => {
  if (!claims) return [];
  
  const rolesClaim = (claims['roles'] || claims['role']) as unknown;
  if (Array.isArray(rolesClaim)) {
    return rolesClaim.filter((v): v is string => typeof v === 'string');
  }
  if (typeof rolesClaim === 'string') {
    return [rolesClaim];
  }
  
  const groupsClaim = claims['groups'] as unknown;
  if (Array.isArray(groupsClaim)) {
    return groupsClaim.filter((v): v is string => typeof v === 'string');
  }
  
  return [];
};

const logTokenDebugInfo = (token: string, type: string): void => {
  if (!import.meta.env.DEV) return;
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      logger.debug(`${type} Token Info`, {
        category: 'api',
        data: {
          scope: payload.scp || payload.scope || 'N/A',
          aud: payload.aud,
          iss: payload.iss,
          exp: new Date(payload.exp * 1000).toISOString()
        }
      });
    }
  } catch {
    // Игнорируем ошибки декодирования
  }
};

const fetchWithRetry = async (
  url: string,
  headers: Record<string, string>,
  maxRetries: number = 3,
  baseDelay: number = 500
): Promise<Response | null> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, { headers });
      if (response.ok || response.status === 404) {
        return response;
      }
      if (response.status >= 500 && attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        logger.warn(`Fetch attempt ${attempt + 1} failed, retrying in ${delay}ms...`, { category: 'api' });
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
  return null;
};

export const useAuth = (): AuthState & {
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  getApiAccessToken: () => Promise<string | null>;
} => {
  const { instance, accounts, inProgress } = useMsal();
  // Активный аккаунт — источник истины. useAccount() без аргументов подписывается
  // на активный аккаунт MSAL и реактивно обновляется при его смене.
  const account = useAccount() ?? accounts[0] ?? null;
  const photoUrlRef = useRef<string | null>(null);
  const initialRoles: readonly string[] = (() => {
    try {
      const claims = (account as unknown as { idTokenClaims?: Record<string, unknown> } | undefined)?.idTokenClaims
        || (instance.getActiveAccount()?.idTokenClaims as Record<string, unknown> | undefined);
      return extractRolesFromClaims(claims);
    } catch {
      return [];
    }
  })();

  const hasAccountInitially = Array.isArray(accounts) && accounts.length > 0;

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: hasAccountInitially,
    user: null,
    userPhoto: null,
    isLoading: inProgress !== InteractionStatus.None,
    isReady: inProgress === InteractionStatus.None,
    error: null,
    roles: initialRoles
  });

  const login = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      logger.error('Login failed', { category: 'api', data: error });
      setAuthState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: 'Ошибка входа в систему' 
      }));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      try { localStorage.removeItem('plng_user_oid'); } catch { /* ignore */ }
      clearTelemetryUser();
      await instance.logoutRedirect();
    } catch (error) {
      logger.error('Logout failed', { category: 'api', data: error });
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (redirectInProgress) {
          logger.debug('getAccessToken: redirect already in progress, waiting...', { category: 'api' });
          await redirectInProgress;
          return null;
        }
        try {
          logger.debug('getAccessToken: initiating redirect...', { category: 'api' });
          redirectInProgress = instance.acquireTokenRedirect(loginRequest);
          await redirectInProgress;
          return null;
        } catch (redirectError) {
          logger.error('Token acquisition failed', { category: 'api', data: redirectError });
          return null;
        } finally {
          redirectInProgress = null;
        }
      }
      logger.error('Silent token acquisition failed', { category: 'api', data: error });
      return null;
    }
  }, [account, instance]);

  const getApiAccessToken = useCallback(async (): Promise<string | null> => {
    const activeAccount = account ?? await waitForAccount(instance, { timeoutMs: 5000 });
    if (!activeAccount) {
      logger.warn('getApiAccessToken: account не готов после ожидания', {
        category: 'api',
        data: {
          accountsLength: instance.getAllAccounts().length,
          inProgress
        }
      });
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: activeAccount,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (redirectInProgress) {
          logger.debug('getApiAccessToken: redirect already in progress, waiting...', { category: 'api' });
          await redirectInProgress;
          return null;
        }
        try {
          logger.debug('Требуется интерактивная аутентификация, выполняем redirect...', { category: 'api' });
          redirectInProgress = instance.acquireTokenRedirect(apiTokenRequest);
          await redirectInProgress;
          return null;
        } catch (redirectError) {
          const redirectErrorMessage = redirectError instanceof Error ? redirectError.message : String(redirectError);
          logger.error('Ошибка при получении токена через redirect', { category: 'api', data: redirectErrorMessage });
          return null;
        } finally {
          redirectInProgress = null;
        }
      }
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorName = error instanceof Error ? error.name : 'Unknown';
      logger.error('Silent получение токена не удалось', {
        category: 'api',
        data: {
          name: errorName,
          message: errorMessage,
          account: activeAccount ? activeAccount.username : 'null',
          accountsLength: instance.getAllAccounts().length
        }
      });
      return null;
    }
  }, [account, instance, inProgress]);

  const extractRolesFromIdToken = useCallback((): readonly string[] => {
    try {
      const idTokenClaims = (account as unknown as { idTokenClaims?: Record<string, unknown> } | undefined)?.idTokenClaims
        || (instance.getActiveAccount()?.idTokenClaims as Record<string, unknown> | undefined);
      return extractRolesFromClaims(idTokenClaims);
    } catch (e) {
      logger.error('Failed to extract roles from id token', { category: 'api', data: e });
      return [];
    }
  }, [instance, account]);

  const fetchUserProfile = useCallback(async (accessToken: string): Promise<UserProfile | null> => {
    try {
      logTokenDebugInfo(accessToken, 'Graph');

      const response = await fetchWithRetry(
        graphConfig.graphMeEndpoint,
        { Authorization: `Bearer ${accessToken}` }
      );

      if (!response) {
        logger.warn('User profile fetch failed after retries (network issue)', { category: 'api' });
        return null;
      }

      if (response.status === 404) {
        logger.warn('User profile not found (404)', { category: 'api' });
        return null;
      }

      if (!response.ok) {
        logger.warn(`User profile fetch failed with status ${response.status}`, { category: 'api' });
        return null;
      }

      const profile: UserProfile = await response.json();
      return profile;
    } catch (error) {
      logger.warn('Graph API error (profile)', {
        category: 'api',
        data: {
          error: error instanceof Error ? error.message : String(error),
          endpoint: graphConfig.graphMeEndpoint
        }
      });
      return null;
    }
  }, []);

  const fetchUserPhoto = useCallback(async (accessToken: string): Promise<string | null> => {
    try {
      const response = await fetchWithRetry(
        graphConfig.graphPhotoEndpoint,
        { Authorization: `Bearer ${accessToken}` }
      );

      if (!response) {
        return null;
      }

      if (response.status === 404 || response.status === 405) {
        return null;
      }

      if (response.ok) {
        try {
          const blob = await response.blob();
          const photoUrl = URL.createObjectURL(blob);
          return photoUrl;
        } catch (blobError) {
          logger.warn('Failed to process photo blob', { category: 'api', data: blobError });
          return null;
        }
      }

      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      setAuthState(prev => ({ ...prev, isLoading: true, isReady: false }));
    }
  }, [inProgress]);

  useEffect(() => {
    const updateAuthState = async () => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      const hasAccount = accounts && accounts.length > 0 && account;
      if (hasAccount) {
        try {
          setAuthState(prev => ({ ...prev, isLoading: true }));
          const rolesFromId = extractRolesFromIdToken();
          const accessToken = await getAccessToken();
          
          if (accessToken) {
            const profilePromise = fetchUserProfile(accessToken);
            const photoPromise = fetchUserPhoto(accessToken);

            const [profile, photo] = await Promise.all([
              profilePromise.catch(err => {
                logger.error('Profile fetch error', { category: 'api', data: err });
                return null;
              }),
              photoPromise.catch(err => {
                logger.error('Photo fetch error', { category: 'api', data: err });
                return null;
              })
            ]);

            if (photoUrlRef.current && photoUrlRef.current !== photo) {
              URL.revokeObjectURL(photoUrlRef.current);
            }
            photoUrlRef.current = photo;

            setAuthState({
              isAuthenticated: true,
              user: profile,
              userPhoto: photo,
              isLoading: false,
              isReady: true,
              error: null,
              roles: rolesFromId
            });
          } else {
            logger.warn('No access token available', { category: 'api' });
            setAuthState(prev => ({
              isAuthenticated: true,
              user: prev.user ?? null,
              userPhoto: prev.userPhoto ?? null,
              isLoading: false,
              isReady: true,
              error: 'Не удалось получить токен доступа',
              roles: rolesFromId
            }));
          }
        } catch (error) {
          logger.error('Error updating auth state', { category: 'api', data: error });
          setAuthState(prev => ({
            isAuthenticated: true,
            user: prev.user ?? null,
            userPhoto: prev.userPhoto ?? null,
            isLoading: false,
            isReady: true,
            error: 'Ошибка загрузки профиля пользователя',
            roles: extractRolesFromIdToken()
          }));
        }
      } else {
        if (photoUrlRef.current) {
          URL.revokeObjectURL(photoUrlRef.current);
          photoUrlRef.current = null;
        }
        setAuthState({
          isAuthenticated: false,
          user: null,
          userPhoto: null,
          isLoading: false,
          isReady: true,
          error: null,
          roles: []
        });
      }
    };

    updateAuthState();

    return () => {
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
    };
  }, [inProgress, accounts, account, getAccessToken, extractRolesFromIdToken, fetchUserProfile, fetchUserPhoto]);

  return {
    ...authState,
    account,
    login,
    logout,
    getAccessToken,
    getApiAccessToken
  };
};

export { AuthenticatedTemplate, UnauthenticatedTemplate };

